/**
 * g.longText - a long text shortened to its box, with ONE standard "read it
 * whole" button (BSP's ask, Sep 2026: every description / note in every
 * list, main card and details panel alike).
 *
 *   g.longText(text, { title, lines, max, cut })  -> HTML string
 *
 * - `lines` (default 1): the text shows on at most that many lines; 1 =
 *   one line ending in an ellipsis, more = a line clamp (the text's own
 *   line breaks kept).
 * - `max` (characters, default none): a hard cap - the text is cut there
 *   whatever the room (a column that must never show more than a label).
 * - `cut: false`: never shortened - the whole text, wrapped, no button.
 * - `title`: the dialog's title (default "The whole text").
 *
 * The button shows ONLY when the text is really cut: measured (the value's
 * scroll box against its client box) and re-measured whenever the box
 * resizes; a one-line text that carries line breaks counts as cut (its
 * lines were joined), and so does a `max` cap that took something. It
 * opens the whole text in g.alert (line breaks kept), through g.action.
 *
 * Server-rendered markup is the same shape (Latte writes it by hand):
 *
 *   <span class="long-text" long-text data-title="Notes">
 *     <span class="long-text-value">the text</span>
 *     <button type="button" class="long-text-view btn btn-xs btn-soft-secondary btn-icon-round" long-text-view title="Read it whole" aria-label="Read it whole"><i class="ri-eye-line" aria-hidden="true"></i></button>
 *   </span>
 *
 * Any [long-text] that enters the document is measured (one
 * MutationObserver + one ResizeObserver for the page), so a lister row, a
 * details box or a modal body needs no call after rendering.
 *
 * Full reference: docs/libs/genus.md "Long texts".
 */
import { esc } from './core.js';
import { translate } from './translate.js';

export function longText(text, { title = '', lines = 1, max = 0, cut = true } = {}) {
	const full = String(text ?? '').trim();
	if (!full) return '';
	if (cut === false) return `<span class="long-text long-text-whole" long-text><span class="long-text-value">${esc(full)}</span></span>`;
	const cap = max > 0 && full.length > max;
	const shown = cap ? `${full.slice(0, max).trimEnd()}…` : full;
	const n = Math.max(1, parseInt(lines, 10) || 1);
	const attrs = [
		cap ? ` long-text-full="${esc(full)}"` : '',
		n > 1 ? ` long-text-lines="${n}" style="--long-text-lines: ${n};"` : '',
		title ? ` data-title="${esc(title)}"` : '',
	].join('');
	const label = esc(translate('Read it whole'));
	return `<span class="long-text${cap ? ' is-cut' : ''}" long-text${attrs}><span class="long-text-value">${esc(shown)}</span>`
		+ `<button type="button" class="long-text-view btn btn-xs btn-soft-secondary btn-icon-round" long-text-view title="${label}" aria-label="${label}"><i class="ri-eye-line" aria-hidden="true"></i></button></span>`;
}

// Is this long text cut right now? A root not laid out (a hidden tab, a
// collapsed box) keeps its last answer - it is measured again when its
// box gets a size.
function measure(root) {
	if (root.classList.contains('long-text-whole')) return;
	const value = root.querySelector(':scope > .long-text-value');
	if (!value) return;
	if (root.hasAttribute('long-text-full')) { root.classList.add('is-cut'); return; }
	if (!value.getClientRects().length) return;
	const multi = root.hasAttribute('long-text-lines');
	const cut = multi
		? value.scrollHeight > value.clientHeight + 1
		: value.scrollWidth > value.clientWidth + 1 || /\n/.test(value.textContent);
	root.classList.toggle('is-cut', cut);
}

let ro = null;

function each(node, fn) {
	if (!(node instanceof Element)) return;
	if (node.matches('[long-text]')) fn(node);
	node.querySelectorAll('[long-text]').forEach(fn);
}

function watch(root) {
	const value = root.querySelector(':scope > .long-text-value');
	if (!value || value._longTextWatched) return;
	value._longTextWatched = true;
	if (ro) ro.observe(value);
	else measure(root);
}

function unwatch(root) {
	const value = root.querySelector(':scope > .long-text-value');
	if (!value || !value._longTextWatched) return;
	value._longTextWatched = false;
	if (ro) ro.unobserve(value);
}

// Re-measure every long text on the page (after a layout change the
// observers cannot see - a web font landing changes the text's width,
// not its box).
longText.refresh = (scope = document) => each(scope instanceof Element ? scope : document.documentElement, measure);

let started = false;
export function initLongText() {
	if (started || typeof document === 'undefined') return;
	started = true;
	if (typeof ResizeObserver === 'function') {
		ro = new ResizeObserver((entries) => {
			for (const e of entries) if (e.target.parentElement) measure(e.target.parentElement);
		});
	}
	const scan = () => each(document.body, watch);
	if (document.body) scan();
	else document.addEventListener('DOMContentLoaded', scan, { once: true });
	if (typeof MutationObserver === 'function') {
		new MutationObserver((records) => {
			for (const r of records) {
				r.removedNodes.forEach((n) => { if (!n.isConnected) each(n, unwatch); });
				r.addedNodes.forEach((n) => each(n, watch));
			}
		}).observe(document.documentElement, { childList: true, subtree: true });
	}
	if (document.fonts && document.fonts.ready) document.fonts.ready.then(() => longText.refresh());

	document.addEventListener('click', (e) => {
		const btn = e.target.closest('[long-text-view]');
		if (!btn) return;
		const root = btn.closest('[long-text]');
		if (!root) return;
		const value = root.querySelector(':scope > .long-text-value');
		const full = root.getAttribute('long-text-full') ?? (value ? value.textContent : '');
		const title = root.dataset.title || translate('The whole text');
		window.g.action(btn, async () => {
			if (typeof window.g.alert === 'function') await window.g.alert(`<span class="long-text-dialog">${esc(full)}</span>`, { title, html: true });
		});
	});
}
